import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, SproutIcon, FolderTreeIcon, StampIcon, AwardIcon, RocketIcon, Building2Icon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
export const Testimonials = () => {
  const testimonials = [{
    text: '"Organicé toda mi planificación docente en un fin de semana. Ahora sé exactamente qué hacer en cada clase y qué cobrar según el perfil del alumno. Nunca más improviso."',
    author: 'Luciana M.',
    city: 'Buenos Aires — Profesora de italiano particular'
  }, {
    text: '"Las plantillas de ciudadanía cambiaron todo. Preparé a 3 alumnos para la entrevista consular con el vocabulario exacto y los 3 aprobaron. Y encima cobré por programa, no por hora."',
    author: 'Hernán R.',
    city: 'Rosario — Docente de italiano en instituto'
  }, {
    text: '"Adapto la clase según el perfil del alumno en 5 minutos usando las fichas de diagnóstico. Antes me llevaba horas preparar material para cada uno. Ahora está todo resuelto."',
    author: 'Valentina F.',
    city: 'Córdoba — Profesora de italiano online'
  }];
  return <section className="py-12 px-5 bg-bg-alt pt-[30px] pb-[30px]" data-id="element-227">
      <div className="max-w-[860px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-228">
        {testimonials.map((t, i) => <div key={i} className="bg-gris-oscuro border border-gris-medio rounded-xl p-6" data-id="element-229">
            <div className="flex text-amarillo mb-3" data-id="element-230">
              {[...Array(5)].map((_, j) => <StarIcon key={j} size={14} fill="currentColor" data-id="element-231" />)}
            </div>
            <p className="text-[14px] text-[#ccc] leading-relaxed mb-4 italic" data-id="element-232">
              {t.text}
            </p>
            <p className="text-[13px] font-semibold text-blanco" data-id="element-233">{t.author}</p>
            <p className="text-[12px] text-gris-claro" data-id="element-234">{t.city}</p>
          </div>)}
      </div>
    </section>;
};
export const Problems = () => {
  const problems = [{
    title: 'Cada alumno tiene un objetivo distinto',
    desc: 'Ciudadanía, CILS, conversación, trabajo. Sin un sistema para cada perfil, improvisás en cada clase.'
  }, {
    title: 'Los exámenes CILS y CELI son un misterio',
    desc: 'Nunca leíste los criterios reales del examinador. Preparás al alumno con ejercicios sueltos y cruzás los dedos.'
  }, {
    title: 'El alumno de ciudadanía llega con urgencia',
    desc: 'Tiene la entrevista en 3 semanas y vos no tenés un plan intensivo listo para ese escenario. Todo lo armás desde cero.'
  }, {
    title: 'El itañol te consume la clase entera',
    desc: 'El alumno llegó con vocabulario dialectal del abuelo y errores fonéticos que no sabés cómo trabajar sin frustrarlo.'
  }, {
    title: 'El alumno adulto abandona y no sabés por qué',
    desc: 'Se va sin aviso. No detectaste las señales a tiempo y no tenés protocolos de retención para actuar antes.'
  }, {
    title: 'Cobrás por hora y llegaste al techo',
    desc: 'Más alumnos = más horas. No tenés programas cerrados, no tenés productos digitales. Tu ingreso no crece.'
  }, {
    title: 'Material caro o en italiano puro',
    desc: 'Los libros son importados, caros o están en italiano sin contexto argentino. Sin material local, preparás todo vos.'
  }, {
    title: 'Sin sistema de seguimiento',
    desc: 'No registrás los errores recurrentes. No medís el progreso. El alumno no siente que avanza y se va.'
  }];
  return <section className="py-20 px-5 pt-[30px] pb-[30px]" data-id="element-235">
      <div className="max-w-[860px] mx-auto" data-id="element-236">
        <div className="text-center mb-10" data-id="element-237">
          <p className="text-xs tracking-[0.1em] uppercase text-cyan mb-3 font-semibold" data-id="element-238">
            ¿Te pasa esto?
          </p>
          <h2 className="text-[clamp(26px,4vw,36px)] font-extrabold leading-[1.2]" data-id="element-239">
            Si enseñás italiano en Argentina, esto te suena familiar
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-240">
          {problems.map((p, i) => <div key={i} className="bg-gris-oscuro border border-gris-medio border-l-[3px] border-l-rojo rounded-lg p-4 md:p-5" data-id="element-241">
              <p className="text-[14px] font-semibold text-blanco mb-1" data-id="element-242">
                {p.title}
              </p>
              <p className="text-[13px] text-gris-claro leading-relaxed" data-id="element-243">
                {p.desc}
              </p>
            </div>)}
        </div>
        <p className="text-center mt-10 text-lg font-bold text-[#aaa]" data-id="element-244">
          ¿Cuántos meses más vas a enseñar sin un sistema?
        </p>
      </div>
    </section>;
};
export const TargetAudience = () => {
  const audience = [{
    title: 'Profesores que empiezan',
    desc: 'Tenés 1 o 2 años dando clases y querés un método desde el principio, sin aprender a los golpes.',
    icon: SproutIcon,
    tag: 'Para vos si…',
    match: '1–2 años'
  }, {
    title: 'Con experiencia desorganizada',
    desc: 'Sabés enseñar pero trabajás de forma artesanal. Querés sistematizar, cobrar más y no depender solo de las horas.',
    icon: FolderTreeIcon,
    tag: 'Para vos si…',
    match: 'Artesanal'
  }, {
    title: 'Preparás para ciudadanía',
    desc: 'Tenés alumnos que necesitan el A2 para la entrevista consular y querés un sistema listo para ese perfil urgente y rentable.',
    icon: StampIcon,
    tag: 'Para vos si…',
    match: 'A2 consular'
  }, {
    title: 'Preparás certificaciones',
    desc: 'Preparás o querés preparar alumnos para CILS y CELI y necesitás dominar los criterios del examinador antes que tu alumno.',
    icon: AwardIcon,
    tag: 'Para vos si…',
    match: 'CILS / CELI'
  }, {
    title: 'Profesores que quieren escalar',
    desc: 'Querés crear tu primer producto digital, armar programas cerrados y construir ingresos que no dependan de cuántas horas podés trabajar.',
    icon: RocketIcon,
    tag: 'Nuevo',
    match: 'Escala',
    featured: true
  }, {
    title: 'Institutos y particular',
    desc: 'Ya tenés alumnos pero querés mejorar la retención, subir tarifas y diferenciarte con material propio en el mercado.',
    icon: Building2Icon,
    tag: 'Para vos si…',
    match: 'Activo'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut'
      }
    }
  };
  return <section className="py-20 px-5 bg-bg-alt pt-[40px] pb-[40px] relative overflow-hidden" data-id="element-245">
      {/* Soft cyan glow background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-cyan/[0.03] rounded-full blur-[120px] pointer-events-none" data-id="element-246"></div>

      <div className="max-w-[1000px] mx-auto relative" data-id="element-247">
        <div className="text-center mb-10 md:mb-12" data-id="element-248">
          <p className="text-xs tracking-[0.1em] uppercase text-cyan mb-3 font-semibold inline-flex items-center gap-1.5" data-id="element-249">
            <SparklesIcon size={12} data-id="element-250" /> ¿Para quién es?
          </p>
          <h2 className="text-[clamp(26px,4vw,36px)] font-extrabold leading-[1.2]" data-id="element-251">
            Diseñado para vos si querés llevar tu docencia al{' '}
            <span className="text-cyan" data-id="element-252">próximo nivel</span>
          </h2>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5" data-id="element-253">
          {audience.map((a, i) => {
          const Icon = a.icon;
          const isFeatured = a.featured;
          return <motion.div key={i} variants={cardVariants} whileHover={{
            y: -4
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 22
          }} className={`group relative rounded-2xl p-5 md:p-6 transition-all duration-300 cursor-default overflow-hidden ${isFeatured ? 'bg-gradient-to-br from-cyan/[0.08] to-gris-oscuro border-2 border-cyan/50 shadow-[0_0_24px_rgba(0,229,255,0.08)]' : 'bg-gris-oscuro border border-gris-medio hover:border-cyan/40 hover:bg-[#1c1c1c] hover:shadow-[0_0_20px_rgba(0,229,255,0.06)]'}`} data-id="element-254">
                {/* Corner glow on featured */}
                {isFeatured && <div className="absolute -top-12 -right-12 w-32 h-32 bg-cyan/15 rounded-full blur-[40px] pointer-events-none" data-id="element-255"></div>}

                {/* Header row: icon + tag */}
                <div className="flex items-start justify-between mb-3 relative" data-id="element-256">
                  <div className={`flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-300 ${isFeatured ? 'bg-cyan/15 border border-cyan/40 text-cyan shadow-[0_0_16px_rgba(0,229,255,0.2)]' : 'bg-[#222] border border-[#333] text-cyan group-hover:bg-cyan/10 group-hover:border-cyan/30'}`} data-id="element-257">
                    <Icon size={20} strokeWidth={2} data-id="element-258" />
                  </div>

                  <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full ${isFeatured ? 'bg-cyan text-black' : 'bg-[#222] text-gris-claro border border-[#333]'}`} data-id="element-259">
                    {a.tag}
                  </span>
                </div>

                {/* Title */}
                <h3 className={`text-[15px] md:text-[16px] font-bold leading-snug mb-2 transition-colors ${isFeatured ? 'text-cyan' : 'text-blanco'}`} data-id="element-260">
                  {a.title}
                </h3>

                {/* Description */}
                <p className="text-[13px] text-[#aaa] leading-relaxed mb-4" data-id="element-261">
                  {a.desc}
                </p>

                {/* Footer: match chip + arrow */}
                <div className="flex items-center justify-between pt-3 border-t border-gris-medio/40" data-id="element-262">
                  <span className={`text-[10.5px] font-semibold tracking-wide uppercase ${isFeatured ? 'text-cyan/80' : 'text-cyan/60'}`} data-id="element-263">
                    {a.match}
                  </span>
                  <ArrowRightIcon size={14} className={`transition-all duration-300 ${isFeatured ? 'text-cyan translate-x-0' : 'text-gris-claro group-hover:text-cyan group-hover:translate-x-1'}`} data-id="element-264" />
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* CTA closure */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="text-center mt-10" data-id="element-265">
          <a href="#ofertas" className="inline-flex items-center gap-2 text-[13.5px] text-gris-claro hover:text-cyan transition-colors group" data-id="element-266">
            <span data-id="element-267">
              ¿No estás seguro si es para vos? Tenés 7 días de garantía
            </span>
            <ArrowRightIcon size={14} className="transition-transform group-hover:translate-x-1" data-id="element-268" />
          </a>
        </motion.div>
      </div>
    </section>;
};